import React, { useMemo, useCallback } from 'react';
import QuestionField from '../QuestionField';
import { cn, preserveBlankLines } from '@/lib/utils';
import { resolveQuestions } from '@shared/policy-application/questionUtils';
import {
  evaluateConditionalBehavior,
  filterVisibleQuestions,
} from '@shared/policy-application/conditionalLogicUtils';
import { FormParameterValues } from '@client/hooks/useFormParameters';
import { useSelector } from 'react-redux';
import { selectClient } from '@client/model';
import ReactMarkdown from 'react-markdown';

interface QuestionGroupProps {
  group: PolicyFormQuestionGroup;
  questionRegistry: Record<string, PolicyFormQuestion>;
  formData: PolicyFormSubmissionData;
  onSubmissionChange: (questionRef: string, value: PolicyFormSubmissionDataValue) => void;
  parameters?: FormParameterValues;
}

const PolicyApplicationQuestionGroup: React.FC<QuestionGroupProps> = ({
  group,
  questionRegistry,
  formData,
  onSubmissionChange,
  parameters = {},
}) => {
  const client = useSelector(selectClient);

  // Check if the group itself should be visible
  const groupEvaluation = useMemo(
    () => evaluateConditionalBehavior(group.conditional, formData, parameters),
    [group.conditional, formData, parameters],
  );

  // Get only the visible questions
  const visibleQuestionRefs = useMemo(
    () => filterVisibleQuestions(group.questions, questionRegistry, formData, parameters),
    [group.questions, questionRegistry, formData, parameters],
  );

  const visibleQuestions = useMemo(
    () => resolveQuestions(visibleQuestionRefs, questionRegistry),
    [visibleQuestionRefs, questionRegistry],
  );

  // Evaluate conditional behavior for each visible question
  const questionsWithEvaluation = useMemo(
    () =>
      visibleQuestions.map(question => ({
        ...question,
        evaluation: evaluateConditionalBehavior(
          question.conditional,
          formData,
          parameters,
          question.required,
        ),
      })),
    [visibleQuestions, formData, parameters],
  );

  const handleQuestionChange = useCallback(
    (questionRef: string) => (value: PolicyFormSubmissionDataValue) => {
      onSubmissionChange(questionRef, value);
    },
    [onSubmissionChange],
  );

  const getQuestionValue = useCallback(
    (questionRef: string): PolicyFormSubmissionDataValue => {
      if (formData && formData[questionRef] !== undefined && formData[questionRef] !== null) {
        return formData[questionRef];
      }
      return formData?.[questionRef];
    },
    [formData], // removed formPrefillData and onSubmissionChange from dependencies
  );

  // Don't render if the group is not visible
  if (!groupEvaluation.visible || visibleQuestions.length === 0) {
    return null;
  }

  return (
    <div
      className={cn('space-y-6', {
        '!mt-0': group.hidden,
      })}
    >
      {group.title && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 whitespace-pre-wrap">
            {group.title?.replace('{clientFullName}', client?.clientFullName ?? '')}
          </h3>
          {group.description && (
            <div className="mt-1 text-sm text-gray-600">
              {/* this helps to do basic formatting on form definition level using markdown syntax */}
              <ReactMarkdown
                components={{
                  p: ({ node, ...props }) => <p {...props} className="whitespace-pre-wrap" />,
                  strong: ({ node, ...props }) => (
                    <strong
                      className="font-semibold"
                      {...props}
                    />
                  ),
                  em: ({ node, ...props }) => (
                    <em
                      className="italic"
                      {...props}
                    />
                  ),
                  u: ({ node, ...props }) => <u {...props} />,
                  ul: ({ node, ...props }) => (
                    <ul
                      className="list-disc pl-6"
                      {...props}
                    />
                  ),
                  ol: ({ node, ...props }) => (
                    <ol
                      className="list-decimal pl-6"
                      {...props}
                    />
                  ),
                  li: ({ node, ...props }) => (
                    <li
                      className="my-1"
                      {...props}
                    />
                  ),
                }}
              >
                {preserveBlankLines(group.description)}
              </ReactMarkdown>
            </div>
          )}
        </div>
      )}

      <div
        className={cn(
          {
            'grid grid-cols-2 gap-6': group.format === 'grid',
            'flex flex-wrap gap-4': group.format === 'inline',
            'space-y-6': group.format === 'default',
            'items-center': group.align === 'center',
            'items-start': group.align === 'start',
            'items-end': group.align === 'end',
            'h-0 gap-0': group.hidden,
          },
          group.className,
        )}
      >
        {questionsWithEvaluation.map(({ evaluation, ...question }) => (
          <QuestionField
            key={question.questionRef}
            question={{
              ...question,
              // Override the static required with the evaluated required
              required: evaluation.required,
            }}
            value={getQuestionValue(question.questionRef)}
            onChange={handleQuestionChange(question.questionRef)}
            parameters={parameters}
          />
        ))}
      </div>
    </div>
  );
};

export default PolicyApplicationQuestionGroup;
